import { useState, type FormEvent } from 'react'
import { CURRENCY_DISPLAYS, DEFAULT_CURRENCY_DISPLAY, toCurrencyDisplay } from '@billiard/shared'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery, CURRENCY_DISPLAY_KEY } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ErrorState,
  Field,
  Icon,
  IconButton,
  ImageUpload,
  Input,
  Loading,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { RELAY_LETTERS_KEY, type SettingRow } from '@/lib/config'
import { Money } from '@/lib/currency'
import { deleteImage, uploadImage } from '@/lib/uploads'

export function Settings(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Настройки"
        lead="Клубные настройки в формате «ключ — значение». Эти параметры облачно-авторитетны и стекают на терминалы клуба."
      />
      <ClubGate>
        <ClientCard />
        <ModulesCard />
        <RelaySettingsCard />
        <BookingSettingsCard />
        <SettingsEditor />
      </ClubGate>
    </>
  )
}

// ─────────────────────── Реквизиты клуба и карточка клиента ───────────────────────

/**
 * Полная карточка клиента: контакты, банковские реквизиты, сведения о лице
 * (организация / ИП / частное лицо), подписант договора и адреса. Заполняется
 * самим клубом ИЛИ super_admin — данные нужны, чтобы готовить договоры/АКТы.
 * Хранится в тех же key-value settings; общие поля (УНП, юр.название, e-mail,
 * телефон, юр.адрес) переиспользуют ключи «Настроек компании» — единый источник.
 */
type ClientEntity = 'org' | 'ip' | 'individual' | ''

const CLIENT_ENTITIES: { value: ClientEntity; label: string }[] = [
  { value: '', label: '— не выбрано —' },
  { value: 'org', label: 'Организация (ЮЛ)' },
  { value: 'ip', label: 'Индивидуальный предприниматель' },
  { value: 'individual', label: 'Частное лицо' }
]

const LEGAL_FORMS = ['ООО', 'ОАО', 'ЗАО', 'ОДО', 'ЧУП', 'УП', 'ИП', 'Другое']
const BASIS_DOCS = [
  'Устав',
  'Доверенность',
  'Свидетельство о гос. регистрации',
  'Приказ о назначении директора',
  'Другое'
]

// Ключи settings. Общие (orgName/unp/clubEmail/clubPhone/clubAddress) переиспользуем.
const CK = {
  entityType: 'clientEntityType',
  legalForm: 'clientLegalForm',
  orgName: 'orgName',
  unp: 'unp',
  email: 'clubEmail',
  phone: 'clubPhone',
  bankAccount: 'bankAccount',
  bankName: 'bankName',
  bankBic: 'bankBic',
  bankAddress: 'bankAddress',
  basisType: 'signBasisType',
  signatoryName: 'signatoryName',
  signatoryPosition: 'signatoryPosition',
  legalAddress: 'clubAddress',
  postalAddress: 'postalAddress',
  addressesSame: 'addressesSame',
  // Поля, перенесённые из прежней «Настройки компании» (теперь карточка одна).
  website: 'clubWebsite',
  country: 'clubCountry',
  city: 'clubCity',
  currency: 'clubCurrency',
  currencyDisplay: 'currencyDisplay',
  shiftEndTime: 'shiftEndTime',
  supportContacts: 'supportContacts'
} as const

const CLIENT_KEYS = Object.values(CK)

function ClientCard(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const save = useMutate()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [loaded, setLoaded] = useState(false)
  const [saved, setSaved] = useState(false)

  if (list.data && !loaded) {
    const map: Record<string, string> = {}
    for (const r of list.data) map[r.key] = r.value
    const init: Record<string, string> = {}
    for (const k of CLIENT_KEYS) init[k] = map[k] ?? ''
    init[CURRENCY_DISPLAY_KEY] = toCurrencyDisplay(map[CURRENCY_DISPLAY_KEY])
    setDraft(init)
    setLoaded(true)
  }

  const set = (key: string, value: string): void => {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(false)
  }

  const entity = (draft[CK.entityType] ?? '') as ClientEntity
  const isIndividual = entity === 'individual'
  const nameLabel =
    entity === 'org'
      ? 'Полное наименование организации'
      : entity === 'ip'
        ? 'ФИО полностью (ИП)'
        : entity === 'individual'
          ? 'ФИО полностью'
          : 'Наименование организации / ФИО'
  const sameAddr = draft[CK.addressesSame] === '1'

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const ok = await save.mutate(async () => {
      // Если адреса совпадают — почтовый = юридический.
      const toSave: Record<string, string> = { ...draft }
      if (toSave[CK.addressesSame] === '1') toSave[CK.postalAddress] = toSave[CK.legalAddress] ?? ''
      for (const k of CLIENT_KEYS) {
        await api.put(
          `/v1/settings/${encodeURIComponent(k)}`,
          { value: toSave[k] ?? '' },
          { query }
        )
      }
    })
    if (ok) {
      setSaved(true)
      list.reload()
    }
  }

  if (list.loading) return <Loading />

  const text = (key: string, label: string, ph?: string, type = 'text'): React.JSX.Element => (
    <div className="field" key={key}>
      <label className="field__label" htmlFor={`cl-${key}`}>
        {label}
      </label>
      <Input
        id={`cl-${key}`}
        type={type}
        value={draft[key] ?? ''}
        placeholder={ph}
        onChange={(e) => set(key, e.target.value)}
      />
    </div>
  )

  return (
    <form
      className="card"
      style={{ padding: 20, marginBottom: 22 }}
      onSubmit={(e) => void onSave(e)}
    >
      <h3 style={{ marginBottom: 4 }}>Реквизиты клуба и карточка клиента</h3>
      <p className="muted" style={{ marginBottom: 16, fontSize: 13 }}>
        Все реквизиты клуба: для чеков и АКТов, договора и карточки на сайте бронирования. Заполняет
        клуб или мы. Сумма всегда в BYN — «Отображение валюты» меняет только подпись.
      </p>

      <div className="form-grid">
        <Field label="Тип лица" htmlFor="cl-entity">
          <Select
            id="cl-entity"
            value={entity}
            onChange={(e) => set(CK.entityType, e.target.value)}
          >
            {CLIENT_ENTITIES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <h4 style={{ margin: '18px 0 8px' }}>Контакты</h4>
      <div className="form-grid">
        {text(CK.phone, 'Телефон для связи', '+375 25 000-00-00', 'tel')}
        {text(CK.email, 'E-mail для АКТов и юр. документов', 'name@company.com', 'email')}
      </div>

      <h4 style={{ margin: '18px 0 8px' }}>Сведения о лице</h4>
      <div className="form-grid">
        {text(
          CK.orgName,
          nameLabel,
          entity === 'org' ? 'ООО «Данели Фуд»' : 'Иванов Иван Иванович'
        )}
        {!isIndividual && (
          <Field label="Организационно-правовая форма" htmlFor="cl-legalForm">
            <Select
              id="cl-legalForm"
              value={draft[CK.legalForm] ?? ''}
              onChange={(e) => set(CK.legalForm, e.target.value)}
            >
              <option value="">— не выбрано —</option>
              {LEGAL_FORMS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!isIndividual && text(CK.unp, 'УНП', '491512215')}
      </div>

      <h4 style={{ margin: '18px 0 8px' }}>Банковские реквизиты</h4>
      <div className="form-grid">
        {text(CK.bankAccount, 'Расчётный счёт', 'BY00 XXXX 0000 0000 0000 0000 0000')}
        {text(CK.bankName, 'Наименование банка', 'ОАО «Приорбанк»')}
        {text(CK.bankBic, 'BIC', 'PJCBBY2X')}
        {text(CK.bankAddress, 'Адрес банка', 'г. Минск, ул. …')}
      </div>

      {!isIndividual && (
        <>
          <h4 style={{ margin: '18px 0 8px' }}>Лицо, заключающее договор</h4>
          <div className="form-grid">
            <Field label="Документ-основание" htmlFor="cl-basisType">
              <Select
                id="cl-basisType"
                value={draft[CK.basisType] ?? ''}
                onChange={(e) => set(CK.basisType, e.target.value)}
              >
                <option value="">— не выбрано —</option>
                {BASIS_DOCS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
              <span className="field__hint">
                ИП: свидетельство о гос. регистрации. ЮЛ: приказ о назначении директора +
                свидетельство.
              </span>
            </Field>
            {text(CK.signatoryName, 'ФИО лица, заключающего договор', 'Иванов Иван Иванович')}
            {text(CK.signatoryPosition, 'Должность', 'Директор')}
            {/* TODO(tech-debt): загрузка файла-основания (свидетельство/приказ) отложена — нужно
                блоб-хранилище (Vercel Blob/S3) + ссылка, а не base64 в settings (он синкается на
                терминал). См. TECH_DEBT.md. */}
          </div>
        </>
      )}

      <h4 style={{ margin: '18px 0 8px' }}>Адреса</h4>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <input
          type="checkbox"
          checked={sameAddr}
          onChange={(e) => set(CK.addressesSame, e.target.checked ? '1' : '0')}
        />
        <span>Почтовый и юридический адрес совпадают</span>
      </label>
      <div className="form-grid">
        {text(CK.legalAddress, 'Юридический адрес', 'обл., индекс, город, улица, дом')}
        {!sameAddr && text(CK.postalAddress, 'Почтовый адрес', 'обл., индекс, город, улица, дом')}
      </div>

      <h4 style={{ margin: '18px 0 8px' }}>Местоположение и прочее</h4>
      <div className="form-grid">
        {text(CK.country, 'Страна', 'Беларусь')}
        {text(CK.city, 'Город', 'Минск')}
        {text(CK.website, 'Веб-сайт', 'https://example.by')}
        {text(CK.currency, 'Валюта (код)', 'BYN')}
        {text(CK.shiftEndTime, 'Время окончания смены', '03:00')}
        {text(
          CK.supportContacts,
          'Контакты поддержки (при блокировке)',
          'пусто = Telegram @VyacheslavZloy'
        )}
        <Field label="Отображение валюты" htmlFor="cl-currencyDisplay">
          <Select
            id="cl-currencyDisplay"
            value={draft[CK.currencyDisplay] || DEFAULT_CURRENCY_DISPLAY}
            onChange={(e) => set(CK.currencyDisplay, e.target.value)}
          >
            {CURRENCY_DISPLAYS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <span className="field__hint">
            Подпись рядом с суммами. Сумма всегда в BYN — меняется только знак.
          </span>
        </Field>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button variant="primary" busy={save.saving} type="submit">
          Сохранить
        </Button>
        {saved && <span className="muted">Сохранено ✓</span>}
        {save.error && <span className="form-error">{save.error}</span>}
      </div>
    </form>
  )
}

// ─────────────────────── Карточка «Реле PowerAnt» ───────────────────────

/** Порядок контактов реле PowerAnt в порядке физической проводки столов (per-club). */
function RelaySettingsCard(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const save = useMutate()
  const [text, setText] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [saved, setSaved] = useState(false)
  const [validation, setValidation] = useState<string | null>(null)

  // Инициализируем из сохранённого JSON-массива (один раз).
  if (list.data && !loaded) {
    const row = list.data.find((r) => r.key === RELAY_LETTERS_KEY)
    let init = ''
    if (row?.value) {
      try {
        const arr: unknown = JSON.parse(row.value)
        if (Array.isArray(arr)) init = arr.join(' ')
      } catch {
        init = row.value
      }
    }
    setText(init)
    setLoaded(true)
  }

  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean)

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (tokens.some((t) => !/^[A-Z]$/.test(t)))
      return setValidation('Каждый контакт — одна латинская буква (A–Z).')
    if (new Set(tokens).size !== tokens.length)
      return setValidation('Буквы реле не должны повторяться.')
    setValidation(null)
    const ok = await save.mutate(() =>
      api.put(
        `/v1/settings/${encodeURIComponent(RELAY_LETTERS_KEY)}`,
        { value: JSON.stringify(tokens) },
        { query }
      )
    )
    if (ok) {
      setSaved(true)
      list.reload()
    }
  }

  if (list.loading) return <Loading />

  return (
    <form
      className="card"
      style={{ padding: 20, marginBottom: 22 }}
      onSubmit={(e) => void onSave(e)}
    >
      <h3 style={{ marginBottom: 4 }}>Реле PowerAnt</h3>
      <p className="muted" style={{ marginBottom: 16, fontSize: 13 }}>
        Контакты реле в порядке физической проводки. Введите буквы через пробел: первая — стол 1,
        вторая — стол 2 и т.д. Этот порядок задаёт список реле при настройке столов. Пусто =
        стандарт A–H (8 столов). Подобрать соответствие буква↔стол можно «Определителем реле» в
        терминале.
      </p>

      <div className="form-grid">
        <Field label="Порядок контактов" htmlFor="relay-letters" full>
          <Input
            id="relay-letters"
            value={text}
            placeholder="A B C D I J K E F G H M N L"
            style={{ fontFamily: 'var(--f-mono)', letterSpacing: 1 }}
            onChange={(e) => {
              setText(e.target.value)
              setSaved(false)
            }}
          />
          <span className="field__hint">
            {tokens.length === 0
              ? 'Будет использован стандарт A–H.'
              : tokens.map((t, i) => `Стол ${i + 1} → ${t}`).join(' · ')}
          </span>
        </Field>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button variant="primary" busy={save.saving} type="submit">
          Сохранить
        </Button>
        {saved && <span className="muted">Сохранено ✓</span>}
        {validation && <span className="form-error">{validation}</span>}
        {save.error && <span className="form-error">{save.error}</span>}
      </div>
    </form>
  )
}

// ─────────────────────── Карточка настроек бронирования ───────────────────────

/**
 * Часовые пояса для выбора (значение — смещение от UTC в минутах, как хранит
 * бэкенд в settings.bookingUtcOffsetMinutes). Покрывают СНГ/РФ; время в чеках,
 * бронях и Telegram-боте считается по выбранному поясу.
 */
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: '60', label: 'UTC+1 — Варшава, Берлин' },
  { value: '120', label: 'UTC+2 — Калининград, Киев' },
  { value: '180', label: 'UTC+3 — Минск, Москва' },
  { value: '240', label: 'UTC+4 — Самара, Баку, Тбилиси' },
  { value: '300', label: 'UTC+5 — Екатеринбург, Ташкент' },
  { value: '360', label: 'UTC+6 — Омск, Астана, Бишкек' },
  { value: '420', label: 'UTC+7 — Красноярск, Новосибирск' },
  { value: '480', label: 'UTC+8 — Иркутск' },
  { value: '540', label: 'UTC+9 — Якутск' },
  { value: '600', label: 'UTC+10 — Владивосток' },
  { value: '660', label: 'UTC+11 — Магадан' },
  { value: '720', label: 'UTC+12 — Камчатка' }
]

type BookingField = {
  key: string
  label: string
  /** `image` — загрузка файла; в settings всё равно ложится URL строкой. */
  type: 'text' | 'number' | 'password' | 'select' | 'image'
  ph?: string
  options?: { value: string; label: string }[]
}

/** Поля раздела бронирования (дружелюбная форма поверх key-value settings). */
const BOOKING_FIELDS: BookingField[] = [
  { key: 'clubName', label: 'Название клуба (для сайта)', type: 'text', ph: '8Ball Club' },
  { key: 'publicSlug', label: 'Slug публичной страницы', type: 'text', ph: 'my-club' },
  { key: 'clubDescription', label: 'Краткое описание', type: 'text', ph: 'Уютный клуб в центре' },
  { key: 'clubLogoUrl', label: 'Логотип клуба', type: 'image' },
  { key: 'bookingSlotMinutes', label: 'Шаг слота, мин', type: 'number', ph: '60' },
  { key: 'bookingMinMinutes', label: 'Мин. длительность, мин', type: 'number', ph: '60' },
  { key: 'bookingMaxMinutes', label: 'Макс. длительность, мин', type: 'number', ph: '240' },
  {
    key: 'bookingMinLeadMinutes',
    label: 'Бронировать не позднее чем за, мин',
    type: 'number',
    ph: '0 — до самого начала'
  },
  {
    key: 'bookingUtcOffsetMinutes',
    label: 'Часовой пояс заведения',
    type: 'select',
    options: TIMEZONE_OPTIONS
  },
  { key: 'smsOriginator', label: 'Имя отправителя SMS', type: 'text', ph: '8Ball' },
  { key: 'smsLogin', label: 'SMS логин (SmartDelivery)', type: 'text', ph: '' },
  { key: 'smsPassword', label: 'SMS пароль', type: 'password', ph: '' }
]

// ─────────────────────── Доп. модули (самообслуживание owner/admin) ───────────────────────

interface FinanceModuleRow {
  id: string
  name: string
  monthlyPrice: number
}

/**
 * Список платных доп. модулей (Бронирования, Telegram-бот, Ресторанный режим,
 * Штрафы…) и переключатели «включено / выключено» для своего клуба. Включение
 * сразу учитывается при следующем формировании счёта в разделе «Финансы».
 */
function ModulesCard(): React.JSX.Element {
  const query = useConfigQuery()
  const catalog = useFetch<FinanceModuleRow[]>('/v1/modules', undefined, [])
  const enabledList = useFetch<string[]>('/v1/modules/club', query, [query?.clubId])
  const toggle = useMutate()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const enabledIds = new Set(enabledList.data ?? [])

  const onToggle = async (moduleId: string, enabled: boolean): Promise<void> => {
    setPendingId(moduleId)
    const ok = await toggle.mutate(() =>
      api.put(`/v1/modules/club/${moduleId}`, { enabled }, { query })
    )
    setPendingId(null)
    if (ok) enabledList.reload()
  }

  if (catalog.loading || enabledList.loading) return <Loading />
  if (catalog.error) return <ErrorState message={catalog.error} />

  const modules = catalog.data ?? []
  if (modules.length === 0) return <></>

  const total = modules.filter((m) => enabledIds.has(m.id)).reduce((s, m) => s + m.monthlyPrice, 0)

  return (
    <div className="card" style={{ padding: 20, marginBottom: 22 }}>
      <h3 style={{ marginBottom: 4 }}>Дополнительные модули</h3>
      <p className="muted" style={{ marginBottom: 16, fontSize: 13 }}>
        Включённые модули добавляются в сумму следующего счёта в разделе «Финансы».
      </p>
      {modules.map((m) => (
        <label
          key={m.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 0',
            borderTop: '1px solid var(--hairline-2)',
            fontSize: 13.5,
            cursor: toggle.saving && pendingId === m.id ? 'wait' : 'pointer'
          }}
        >
          <input
            type="checkbox"
            checked={enabledIds.has(m.id)}
            disabled={toggle.saving && pendingId === m.id}
            onChange={(e) => void onToggle(m.id, e.target.checked)}
          />
          {m.name}
          <span className="muted" style={{ marginLeft: 'auto' }}>
            <Money value={m.monthlyPrice} />
            /мес
          </span>
        </label>
      ))}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid var(--hairline-2)',
          fontSize: 13
        }}
      >
        <span className="muted">Итого модули</span>
        <span>
          <Money value={total} />
          /мес
        </span>
      </div>
      {toggle.error && (
        <div className="form-error" style={{ marginTop: 10 }}>
          {toggle.error}
        </div>
      )}
    </div>
  )
}

/**
 * Часы работы клуба редактируются по дням недели, а хранятся в settings как JSON
 * `{"0":{"open":"10:00","close":"24:00"}, "6":null}` (ключ — день по JS getDay(),
 * 0=Вс; null = выходной). Формат читают бэкенд и сайты брони — он не меняется,
 * меняется только форма ввода: раньше JSON правили руками прямо в текстовом поле.
 */
interface DayHours {
  /** Работаем в этот день (иначе выходной — в JSON уедет null). */
  open: boolean
  /** 'HH:MM' в 24-часовом формате. */
  from: string
  /** 'HH:MM'; полночь показываем как 00:00, а храним как 24:00 (так требует бэкенд). */
  to: string
}

/** Порядок строк в форме — с понедельника; значение = индекс дня в JSON (getDay). */
const WEEK_ROWS: { day: number; label: string }[] = [
  { day: 1, label: 'Понедельник' },
  { day: 2, label: 'Вторник' },
  { day: 3, label: 'Среда' },
  { day: 4, label: 'Четверг' },
  { day: 5, label: 'Пятница' },
  { day: 6, label: 'Суббота' },
  { day: 0, label: 'Воскресенье' }
]

/** Дефолт бэкенда: ежедневно 10:00–24:00 (см. booking-config.service.ts). */
const DEFAULT_DAY: DayHours = { open: true, from: '10:00', to: '00:00' }

/** '24:00' → '00:00' для <input type="time">; остальное как есть. */
function hhmmToInput(value: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return ''
  const h = Number(m[1])
  return h >= 24 ? '00:00' : `${String(h).padStart(2, '0')}:${m[2]}`
}

/** Минуты от полуночи; '00:00' в поле «до» означает конец суток → 1440. */
function toMinutes(value: string, isClose: boolean): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const min = Number(m[1]) * 60 + Number(m[2])
  if (isClose && min === 0) return 24 * 60
  return min
}

/** settings.bookingWorkingHours (JSON) → строки формы. Битый JSON → дефолт. */
function parseWorkingHours(raw: string): Record<number, DayHours> {
  const out: Record<number, DayHours> = {}
  for (const { day } of WEEK_ROWS) out[day] = { ...DEFAULT_DAY }
  const text = raw.trim()
  if (!text) return out
  try {
    const parsed = JSON.parse(text) as Record<string, { open?: string; close?: string } | null>
    for (const { day } of WEEK_ROWS) {
      if (!(String(day) in parsed)) continue
      const v = parsed[String(day)]
      if (v === null) {
        out[day] = { ...DEFAULT_DAY, open: false }
        continue
      }
      out[day] = {
        open: true,
        from: hhmmToInput(v?.open ?? '') || DEFAULT_DAY.from,
        to: hhmmToInput(v?.close ?? '') || DEFAULT_DAY.to
      }
    }
  } catch {
    // Битый JSON из ручной правки — показываем дефолт, сохранение его перезапишет.
  }
  return out
}

/** Строки формы → JSON для settings (все 7 дней явно, чтобы не было двусмысленности). */
function serializeWorkingHours(hours: Record<number, DayHours>): string {
  const obj: Record<string, { open: string; close: string } | null> = {}
  for (const { day } of WEEK_ROWS) {
    const h = hours[day]
    if (!h?.open) {
      obj[String(day)] = null
      continue
    }
    const close = toMinutes(h.to, true) === 24 * 60 ? '24:00' : h.to
    obj[String(day)] = { open: h.from, close }
  }
  return JSON.stringify(obj)
}

/** Первая ошибка расписания (пустое время, закрытие не позже открытия) или null. */
function validateWorkingHours(hours: Record<number, DayHours>): string | null {
  for (const { day, label } of WEEK_ROWS) {
    const h = hours[day]
    if (!h?.open) continue
    const from = toMinutes(h.from, false)
    const to = toMinutes(h.to, true)
    if (from == null || to == null) return `${label}: укажите время открытия и закрытия.`
    if (to <= from)
      return `${label}: закрытие должно быть позже открытия (работу за полночь укажите как 00:00).`
  }
  return null
}

/** Редактор недельного расписания: строка на день, тумблер «выходной», два поля времени. */
function WorkingHoursEditor({
  hours,
  onChange
}: {
  hours: Record<number, DayHours>
  onChange: (next: Record<number, DayHours>) => void
}): React.JSX.Element {
  const setDay = (day: number, patch: Partial<DayHours>): void =>
    onChange({ ...hours, [day]: { ...hours[day], ...patch } })

  const copyMondayToAll = (): void => {
    const src = hours[1]
    const next: Record<number, DayHours> = {}
    for (const { day } of WEEK_ROWS) next[day] = { ...src }
    onChange(next)
  }

  return (
    <div className="field field--full">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <label className="field__label" style={{ margin: 0 }}>
          Часы работы
        </label>
        <button type="button" className="btn btn--ghost btn--sm" onClick={copyMondayToAll}>
          Как в понедельник — всю неделю
        </button>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {WEEK_ROWS.map(({ day, label }) => {
          const h = hours[day] ?? DEFAULT_DAY
          return (
            <div
              key={day}
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
            >
              <label
                className="checkbox"
                style={{ margin: 0, flex: '0 0 150px' }}
                title={`${label}: работаем или выходной`}
              >
                <input
                  type="checkbox"
                  checked={h.open}
                  onChange={(e) => setDay(day, { open: e.target.checked })}
                />
                <span>{label}</span>
              </label>
              {h.open ? (
                <>
                  <Input
                    type="time"
                    aria-label={`${label}: открытие`}
                    style={{ width: 116 }}
                    value={h.from}
                    onChange={(e) => setDay(day, { from: e.target.value })}
                  />
                  <span className="muted">—</span>
                  <Input
                    type="time"
                    aria-label={`${label}: закрытие`}
                    style={{ width: 116 }}
                    value={h.to}
                    onChange={(e) => setDay(day, { to: e.target.value })}
                  />
                  {h.to === '00:00' && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      до полуночи
                    </span>
                  )}
                </>
              ) : (
                <span className="muted">Выходной — онлайн-брони на этот день не принимаются</span>
              )}
            </div>
          )
        })}
      </div>
      <span className="field__hint">
        Время в часовом поясе заведения. Закрытие в 00:00 — это полночь (конец суток); работу после
        полуночи пока задать нельзя.
      </span>
    </div>
  )
}

function BookingSettingsCard(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const save = useMutate()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [enabled, setEnabled] = useState(false)
  const [allowTableChoice, setAllowTableChoice] = useState(false)
  const [hours, setHours] = useState<Record<number, DayHours>>(() => parseWorkingHours(''))
  const [loaded, setLoaded] = useState(false)
  const [saved, setSaved] = useState(false)
  const [hoursError, setHoursError] = useState<string | null>(null)

  // Инициализируем черновик из загруженных настроек (один раз).
  if (list.data && !loaded) {
    const map: Record<string, string> = {}
    for (const r of list.data) map[r.key] = r.value
    const init: Record<string, string> = {}
    for (const f of BOOKING_FIELDS) init[f.key] = map[f.key] ?? ''
    setDraft(init)
    setEnabled(['1', 'true', 'yes', 'on'].includes((map.publicBookingEnabled ?? '').toLowerCase()))
    setAllowTableChoice(
      ['1', 'true', 'yes', 'on'].includes((map.bookingAllowTableChoice ?? '').toLowerCase())
    )
    setHours(parseWorkingHours(map.bookingWorkingHours ?? ''))
    setLoaded(true)
  }

  const set = (key: string, value: string): void => {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(false)
  }

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const invalid = validateWorkingHours(hours)
    setHoursError(invalid)
    if (invalid) return
    const entries: { key: string; value: string }[] = [
      ...BOOKING_FIELDS.map((f) => ({ key: f.key, value: draft[f.key] ?? '' })),
      { key: 'publicBookingEnabled', value: enabled ? '1' : '0' },
      { key: 'bookingAllowTableChoice', value: allowTableChoice ? '1' : '0' },
      { key: 'bookingWorkingHours', value: serializeWorkingHours(hours) }
    ]
    const ok = await save.mutate(async () => {
      for (const { key, value } of entries) {
        await api.put(`/v1/settings/${encodeURIComponent(key)}`, { value }, { query })
      }
    })
    if (ok) {
      setSaved(true)
      list.reload()
    }
  }

  if (list.loading) return <Loading />

  return (
    <form
      className="card"
      style={{ padding: 20, marginBottom: 22 }}
      onSubmit={(e) => void onSave(e)}
    >
      <h3 style={{ marginBottom: 4 }}>Бронирование и публичная страница</h3>
      <p className="muted" style={{ marginBottom: 16, fontSize: 13 }}>
        Параметры онлайн-брони. Публичная страница доступна по адресу{' '}
        <code>/{draft.publicSlug || 'slug'}</code> на сайте бронирования.
      </p>

      <div className="form-grid">
        <label
          className="field field--full"
          style={{ display: 'flex', gap: 10, alignItems: 'center' }}
        >
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="field__label" style={{ margin: 0 }}>
            Принимать онлайн-брони с публичной страницы
          </span>
        </label>

        <label
          className="field field--full"
          style={{ display: 'flex', gap: 10, alignItems: 'center' }}
        >
          <input
            type="checkbox"
            checked={allowTableChoice}
            onChange={(e) => setAllowTableChoice(e.target.checked)}
          />
          <span className="field__label" style={{ margin: 0 }}>
            Давать клиенту выбирать конкретный стол (иначе стол назначается автоматически)
          </span>
        </label>

        {BOOKING_FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label className="field__label" htmlFor={`bs-${f.key}`}>
              {f.label}
            </label>
            {f.type === 'image' ? (
              <ImageUpload
                value={draft[f.key] ?? ''}
                onChange={(url) => set(f.key, url)}
                onPick={(file) => uploadImage(file, 'club-logo', query)}
                onRemove={(url) => deleteImage(url, query)}
                hint="Показывается на сайте брони и в Telegram. JPEG, PNG, GIF или WebP, до 5 МБ. Не забудьте сохранить."
              />
            ) : f.type === 'select' ? (
              <Select
                id={`bs-${f.key}`}
                value={draft[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
              >
                <option value="">— выберите часовой пояс —</option>
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id={`bs-${f.key}`}
                type={f.type}
                value={draft[f.key] ?? ''}
                placeholder={f.ph}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </div>
        ))}

        <SmsSelfTest />

        <WorkingHoursEditor
          hours={hours}
          onChange={(next) => {
            setHours(next)
            setHoursError(null)
            setSaved(false)
          }}
        />
        {hoursError && <div className="form-error field--full">{hoursError}</div>}
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button variant="primary" busy={save.saving} type="submit">
          Сохранить
        </Button>
        {saved && <span className="muted">Сохранено ✓</span>}
        {save.error && <span className="form-error">{save.error}</span>}
      </div>
    </form>
  )
}

function SettingsEditor(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const [editing, setEditing] = useState<SettingRow | 'new' | null>(null)

  const rows = [...(list.data ?? [])].sort((a, b) => a.key.localeCompare(b.key))

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} параметр(ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый параметр
          </Button>
        </div>
      </div>

      <AsyncState query={list} isEmpty={rows.length === 0} empty="Параметры ещё не заданы." />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Ключ</th>
                <th>Значение</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.key}>
                  <td style={{ fontFamily: 'var(--f-mono)' }}>{s.key}</td>
                  <td>{s.value}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(s)}>
                        {Icon.edit}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SettingForm
          setting={editing === 'new' ? null : editing}
          existingKeys={rows.map((r) => r.key)}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}
    </>
  )
}

function SettingForm({
  setting,
  existingKeys,
  query,
  onClose,
  onSaved
}: {
  setting: SettingRow | null
  existingKeys: string[]
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = setting !== null
  const [key, setKey] = useState(setting?.key ?? '')
  const [value, setValue] = useState(setting?.value ?? '')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmedKey = key.trim()
    if (!trimmedKey) return setValidation('Укажите ключ параметра.')
    if (!isEdit && existingKeys.includes(trimmedKey))
      return setValidation('Параметр с таким ключом уже существует.')
    setValidation(null)

    const ok = await save.mutate(() =>
      api.put(`/v1/settings/${encodeURIComponent(trimmedKey)}`, { value }, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить параметр' : 'Новый параметр'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="setting-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="setting-form" onSubmit={(e) => void onSubmit(e)}>
        <Field label="Ключ" htmlFor="s-key">
          <Input
            id="s-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="напр. printer.width"
            disabled={isEdit}
            autoFocus={!isEdit}
            style={{ fontFamily: 'var(--f-mono)' }}
          />
          {isEdit && <span className="field__hint">Ключ нельзя изменить.</span>}
        </Field>
        <Field label="Значение" htmlFor="s-value">
          <textarea
            id="s-value"
            className="input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="строковое значение"
            autoFocus={isEdit}
          />
        </Field>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

/**
 * Проверка SMS-шлюза без реальной брони.
 *
 * До этого блока «проверить SMS» можно было только настоящей бронью, и её отказ
 * выглядел как обобщённое «не удалось отправить SMS»: по нему нельзя отличить
 * закрытый IP-whitelist провайдера от неверного пароля или от включённого
 * тестового режима. Здесь показываем сырой ответ шлюза — его и отправляют в
 * поддержку провайдера.
 */
function SmsSelfTest(): React.JSX.Element {
  const query = useConfigQuery()
  const status = useFetch<{ dryRun: boolean; credsSource: string; message: string }>(
    '/v1/sms/status',
    query,
    [query?.clubId]
  )
  const [phone, setPhone] = useState('')
  const [result, setResult] = useState<{
    ok: boolean
    dryRun: boolean
    providerCode: string | null
    error: string | null
    msisdn: string
  } | null>(null)
  const test = useMutate()

  const run = async (): Promise<void> => {
    setResult(null)
    await test.mutate(async () => {
      const res = await api.post<{
        ok: boolean
        dryRun: boolean
        providerCode: string | null
        error: string | null
        msisdn: string
      }>('/v1/sms/test', { phone }, { query })
      setResult(res)
    })
    status.reload()
  }

  return (
    <div className="field field--full">
      <label className="field__label">Проверка SMS</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Input
          value={phone}
          placeholder="+375 29 123-45-67"
          onChange={(e) => setPhone(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <Button
          variant="ghost"
          type="button"
          busy={test.saving}
          disabled={phone.trim().length < 5}
          onClick={() => void run()}
        >
          Отправить проверочное SMS
        </Button>
      </div>
      {status.data && (
        <span className={status.data.dryRun ? 'form-error' : 'field__hint'}>
          {status.data.message}
        </span>
      )}
      {test.error && <span className="form-error">{test.error}</span>}
      {result && (
        <span className={result.ok && !result.dryRun ? 'field__hint' : 'form-error'}>
          {result.dryRun
            ? 'Тестовый режим: сообщение не отправлено, код есть только в логах сервера.'
            : result.ok
              ? `Отправлено на ${result.msisdn} (код шлюза ${result.providerCode ?? '0'}).`
              : `Шлюз отказал: ${result.error ?? 'неизвестная ошибка'}` +
                (result.providerCode ? ` (код ${result.providerCode})` : '')}
        </span>
      )}
    </div>
  )
}
